// import stylesheet if you're not already using CSS @import
import 'react-image-gallery/styles/css/image-gallery.css';

import { Image } from 'expo-image';
import * as React from 'react';
import { Dimensions, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { ArrowRight, Docs } from '@/ui/icons';
import { ArrowLeft } from '@/ui/icons/arrow-left';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { MenuItem } from '@/ui/menu-item';

export interface MediaPreviewerProps {
  title: string;
  pictures: string[];
  pdfs: any;
  onOpen?: () => void;
  carouselHeight?: number;
  carouselWidth?: number;
}

const PAGE_WIDTH = Dimensions.get('window').width;
const PAGE_HEIGHT = Dimensions.get('window').height;

export default function MediaPreviewer({
  title,
  pictures,
  pdfs,
  onOpen,
  carouselHeight,
  carouselWidth,
}: MediaPreviewerProps) {
  const ref = React.useRef<ICarouselInstance>(null);
  const { styles, theme } = useStyles(stylesheet);
  const [showGallery, setShowGallery] = React.useState(false);
  const [activePictureIdx, setActivePictureIdx] = React.useState(0);
  const progress = useSharedValue<number>(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: true,
    });

    setActivePictureIdx(index);
  };

  if (showGallery && pictures) {
    if (isDesktop()) {
      return (
        <Modal
          animationIn="slideInUp"
          animationInTiming={300}
          animationOut="slideOutDown"
          animationOutTiming={1000}
          isVisible={showGallery}
          hasBackdrop
          onBackdropPress={() => {
            setShowGallery(false);
          }}
        >
          <View style={{ justifyContent: 'center', alignItems: 'center' }}>
            <View
              style={{
                height: 600,
                width: 800,
                backgroundColor: theme.colors.background_2,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                paddingLeft: 16,
                paddingRight: 16,
              }}
            >
              <View
                style={{
                  alignSelf: 'center',
                  marginTop: 16,
                  flexDirection: 'row',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <Text type="title2">
                  {pictures[activePictureIdx].split('/').pop()}
                </Text>

                <NavigationButton
                  type={NavigationButtonTypes.cancel}
                  onPress={() => {
                    setShowGallery(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: 0,
                  }}
                />
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Pressable
                  onPress={() => {
                    carouselRef.current?.prev();
                  }}
                >
                  <Icon
                    type="dressed"
                    icon={<ArrowLeft />}
                    fillColor={theme.colors.icon.color}
                  />
                </Pressable>

                <View>
                  <Carousel
                    ref={carouselRef}
                    width={400}
                    height={600 * 0.85}
                    data={pictures}
                    onProgressChange={progress}
                    onSnapToItem={(index) => {
                      setActivePictureIdx(index);
                    }}
                    loop={false}
                    defaultIndex={activePictureIdx}
                    snapEnabled
                    mode="parallax"
                    modeConfig={{
                      parallaxScrollingScale: 0.95,
                      parallaxScrollingOffset: 10,
                    }}
                    // style={{ width: 400, height: PAGE_HEIGHT * 0.9 }}
                    renderItem={({ item }) => {
                      return (
                        <View
                          style={{
                            flexGrow: 1,
                          }}
                        >
                          <Image
                            style={{ width: '100%', height: '100%' }}
                            source={item}
                            contentFit="contain"
                          />
                        </View>
                      );
                    }}
                  />

                  <Pagination.Basic
                    progress={progress}
                    data={pictures}
                    dotStyle={{
                      backgroundColor: theme.colors.paginationDots.empty,
                      borderRadius: 50,
                    }}
                    activeDotStyle={{
                      backgroundColor: theme.colors.paginationDots.filled,
                    }}
                    size={8}
                    containerStyle={{ gap: 10, marginTop: 20 }}
                    onPress={onPressPagination}
                  />
                </View>

                <Pressable
                  onPress={() => {
                    carouselRef.current?.next();
                  }}
                >
                  <Icon
                    type="dressed"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.icon.color}
                  />
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      );
    }

    return (
      <View
        style={{
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Carousel
          ref={ref}
          width={carouselWidth || PAGE_WIDTH}
          height={carouselHeight || PAGE_HEIGHT * 0.8}
          data={pictures}
          onProgressChange={progress}
          onSnapToItem={(index) => {
            setActivePictureIdx(index);
          }}
          loop={false}
          defaultIndex={activePictureIdx}
          snapEnabled
          mode="parallax"
          modeConfig={{
            parallaxScrollingScale: 0.95,
            parallaxScrollingOffset: 10,
          }}
          renderItem={({ item }) => {
            return (
              <View
                style={{
                  flexGrow: 1,
                }}
              >
                <Image
                  style={{ width: '100%', height: '100%' }}
                  source={item}
                  contentFit="contain"
                />
              </View>
            );
          }}
        />

        <View>
          <Text type="bodyRegular">{`${activePictureIdx + 1} / ${
            pictures.length
          }`}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={{ gap: 40 }}>
      {pictures?.length > 0 ? (
        <View style={{ gap: 8 }}>
          <Text tx="mediaViewer.pictures" type="title2" />

          <ScrollView
            horizontal
            contentContainerStyle={styles.picturesContainer}
            showsHorizontalScrollIndicator={false}
          >
            {pictures?.map((picture: string, idx: number) => (
              <View key={`${picture}-${idx}`} style={{ width: 130 }}>
                <Pressable
                  style={{
                    width: 130,
                    height: 140,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                  onPress={() => {
                    setShowGallery(true);
                    setActivePictureIdx(idx);
                    onOpen?.();
                  }}
                >
                  <Image
                    source={picture}
                    contentFit="contain"
                    style={{
                      width: '100%',
                      aspectRatio: 1,
                      borderRadius: 8,
                    }}
                  />
                </Pressable>

                <Text
                  numberOfLines={1}
                  style={{ color: theme.colors.text.secondary }}
                  type="caption1"
                >
                  {picture.split('/').pop()}
                </Text>
              </View>
            ))}
          </ScrollView>
        </View>
      ) : null}

      {pdfs.length > 0 ? (
        <View style={{ gap: 8 }}>
          <Text tx="mediaViewer.pdf" type="title2" />

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ gap: 8 }}
          >
            {pdfs.map((pdf: string, idx: number) => (
              <MenuItem
                key={`${pdf}-${idx}`}
                leftIcon={<Docs />}
                leftIconFillColor={theme.colors.icon.color}
                rightIcon={<ChevronRight />}
                tx="mediaViewer.pdfName"
                values={{ pdfName: pdf.split('/').pop() }}
                onPress={() => {
                  onOpen?.();
                  window.open(pdf, '_blank');
                }}
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: theme.colors.card.default.borderColor,
                }}
              />
            ))}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  picturesContainer: {
    gap: 16,
    width: {
      xs: PAGE_WIDTH - 32,
      md: 'auto',
    },
  },
}));
